import { AvatarUpload } from '@/components/shared/avatar-upload';
import { ensureValidToken } from '@/libs/auth-guard';
import { DataListItem, DataListRoot } from '@/components/ui/data-list';
import { PageHeader } from '@/components/ui';
import { toaster } from '@/components/ui/toaster';
import {
  deleteBrandImage,
  fetchCurrentBrandDetails,
  uploadBrandImage,
} from '@/services/brand-service';
import { usePageTitleStore } from '@/stores/page-title';
import { Box, Button, Link, Separator, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { isEuMemberCountry, type Brand } from '@fluenceur/types';
import { EditPencil } from 'iconoir-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import BrandInformationFormModal from './components/BrandInformationFormModal';
import CurrencyPreferenceSection from './components/CurrencyPreferenceSection';
import LanguagePreferenceSection from './components/LanguagePreferenceSection';
import OutreachEmailSection from './components/OutreachEmailSection';
import { BrandTermsSection } from './components/BrandTermsSection';
import SettingsSection from './components/SettingsSection';

const GeneralSettingsPage = () => {
  const { t } = useTranslation();
  const brand = useLoaderData() as Brand;
  const { user } = useAuth();
  const isShopifyConnected = !!user?.integrations?.shopify?.isConnected;
  const [isModalFormOpen, setIsModalFormOpen] = useState(false);
  const [brandImageUrl, setBrandImageUrl] = useState<string | undefined>(brand.imageUrl || '');

  const setTitle = usePageTitleStore((state) => state.setTitle);

  useEffect(() => {
    setTitle(t('settings.general'));
    return () => setTitle('');
  }, [setTitle, t]);

  const handleImageUploaded = (imageUrl: string) => {
    setBrandImageUrl(imageUrl);
    toaster.success({
      title: t('settings.generalPage.imageUpdatedTitle'),
      description: t('settings.generalPage.imageUpdatedDescription'),
    });
  };

  return (
    <Box maxW="1000px" w="full">
      <PageHeader title={t('settings.general')} titleSize="xl" marginBottom={4} />

      <SettingsSection
        title={t('settings.brandInformation')}
        layout="stacked"
        actions={
          <Button variant="outline" size="sm" onClick={() => setIsModalFormOpen(true)}>
            <EditPencil width={20} height={20} /> {t('common.edit')}
          </Button>
        }
      >
        <DataListRoot orientation="vertical" gap={6}>
          <Box>
            <AvatarUpload
              initialImageUrl={brandImageUrl}
              onImageUploaded={handleImageUploaded}
              size="md"
              customUploadFn={uploadBrandImage}
              customDeleteFn={deleteBrandImage}
              queryKey={['brandDetails']}
              label={t('settings.brandImage')}
            />
          </Box>
          <Separator />
          <SimpleGrid columns={[1, null, 2]} gap={6}>
            <DataListItem
              label={t('common.name')}
              alignItems="baseline"
              value={
                <Text fontWeight="medium" fontSize="md">
                  {brand.name || '-'}
                </Text>
              }
            />
            <DataListItem
              label={t('settings.website')}
              alignItems="baseline"
              value={
                brand.websiteUrl ? (
                  <Link href={brand.websiteUrl} target="_blank" color="primary.focusRing">
                    <Text fontWeight="medium" fontSize="md">
                      {brand.websiteUrl}
                    </Text>
                  </Link>
                ) : (
                  <Text fontWeight="medium" fontSize="md" color="fg.muted">
                    -
                  </Text>
                )
              }
            />
            <DataListItem
              label={t('settings.registrationNumber')}
              alignItems="baseline"
              value={
                <Text
                  fontWeight="medium"
                  fontSize="md"
                  color={brand.companyDetails?.registrationNumber ? 'inherit' : 'fg.muted'}
                >
                  {brand.companyDetails?.registrationNumber || '-'}
                </Text>
              }
            />
            {/* Shown to a brand in the union, and to any brand that still holds
                a number. A value that prints on a document must never be
                invisible on the screen that owns it: a brand that moved out of
                the union keeps whatever it typed, and hiding it would leave it
                unable to withdraw what its invoices go on stating. */}
            {(isEuMemberCountry(brand.companyDetails?.country) ||
              !!brand.companyDetails?.vatNumber) && (
              <DataListItem
                label={t('settings.vatNumber')}
                alignItems="baseline"
                value={
                  <Text
                    fontWeight="medium"
                    fontSize="md"
                    color={brand.companyDetails?.vatNumber ? 'inherit' : 'fg.muted'}
                  >
                    {brand.companyDetails?.vatNumber || '-'}
                  </Text>
                }
              />
            )}
            <DataListItem
              label={t('settings.address')}
              alignItems="baseline"
              value={
                brand.companyDetails?.addressLine1 ? (
                  <VStack alignItems="start" gap={0}>
                    <Text fontWeight="medium" fontSize="md">
                      {brand.companyDetails.addressLine1}
                    </Text>
                    {brand.companyDetails.addressLine2 && (
                      <Text fontWeight="medium" fontSize="md">
                        {brand.companyDetails.addressLine2}
                      </Text>
                    )}
                    <Text fontWeight="medium" fontSize="md">
                      {brand.companyDetails.postalCode}
                      {brand.companyDetails.city ? `, ${brand.companyDetails.city}` : ''}
                    </Text>
                    {brand.companyDetails.country && (
                      <Text fontWeight="medium" fontSize="md">
                        {brand.companyDetails.country}
                      </Text>
                    )}
                  </VStack>
                ) : (
                  <Text fontWeight="medium" fontSize="md" color="fg.muted">
                    -
                  </Text>
                )
              }
            />
          </SimpleGrid>
        </DataListRoot>
      </SettingsSection>

      <LanguagePreferenceSection brand={brand} />
      <CurrencyPreferenceSection brand={brand} isShopifyConnected={isShopifyConnected} />
      <OutreachEmailSection brand={brand} />
      <BrandTermsSection brand={brand} />

      <BrandInformationFormModal
        isOpen={isModalFormOpen}
        onClose={() => setIsModalFormOpen(false)}
        defaultValues={{
          name: brand.name,
          websiteUrl: brand.websiteUrl || '',
          city: brand.companyDetails?.city || '',
          addressLine1: brand.companyDetails?.addressLine1 || '',
          addressLine2: brand.companyDetails?.addressLine2 || null,
          postalCode: brand.companyDetails?.postalCode || '',
          country: brand.companyDetails?.country || '',
          registrationNumber: brand.companyDetails?.registrationNumber || '',
          vatNumber: brand.companyDetails?.vatNumber || '',
        }}
      />
    </Box>
  );
};

export const generalSettingsLoader = async () => {
  await ensureValidToken();
  return await fetchCurrentBrandDetails();
};

export default GeneralSettingsPage;
